/**
 * OWL2 Class Expression Types
 *
 * Defines all 18 class expression types from the OWL2 specification:
 * - Named class (Class)
 * - Boolean combinations: ObjectIntersectionOf, ObjectUnionOf, ObjectComplementOf, ObjectOneOf
 * - Object property restrictions: ObjectSomeValuesFrom, ObjectAllValuesFrom, ObjectHasValue,
 *   ObjectHasSelf, ObjectMinCardinality, ObjectMaxCardinality, ObjectExactCardinality
 * - Data property restrictions: DataSomeValuesFrom, DataAllValuesFrom, DataHasValue,
 *   DataMinCardinality, DataMaxCardinality, DataExactCardinality
 */

import type { IRI } from './IRI.ts';
import type { Individual } from './Entity.ts';
import type { ObjectPropertyExpression, DataPropertyExpression } from './PropertyExpression.ts';
import type { DataRange } from './DataRange.ts';
import type { Literal } from './Literal.ts';

// =============================================================================
// Class Expression Type Union
// =============================================================================

/**
 * All possible class expression types.
 */
export type ClassExpressionType =
    | 'Class'
    // Boolean combinations
    | 'ObjectIntersectionOf'
    | 'ObjectUnionOf'
    | 'ObjectComplementOf'
    | 'ObjectOneOf'
    // Object property restrictions
    | 'ObjectSomeValuesFrom'
    | 'ObjectAllValuesFrom'
    | 'ObjectHasValue'
    | 'ObjectHasSelf'
    | 'ObjectMinCardinality'
    | 'ObjectMaxCardinality'
    | 'ObjectExactCardinality'
    // Data property restrictions
    | 'DataSomeValuesFrom'
    | 'DataAllValuesFrom'
    | 'DataHasValue'
    | 'DataMinCardinality'
    | 'DataMaxCardinality'
    | 'DataExactCardinality';

/**
 * Base interface for all class expressions.
 */
interface ClassExpressionBase {
    readonly type: ClassExpressionType;
}

// =============================================================================
// Named Class
// =============================================================================

/**
 * Named class - references a class by IRI.
 * OWL2: Class(IRI)
 */
export interface Class extends ClassExpressionBase {
    readonly type: 'Class';
    readonly iri: IRI;
}

// =============================================================================
// Boolean Combinations
// =============================================================================

/**
 * Intersection of class expressions (AND).
 * All class expressions must be satisfied.
 * OWL2: ObjectIntersectionOf(CE1 ... CEn) - requires at least 2 operands.
 */
export interface ObjectIntersectionOf extends ClassExpressionBase {
    readonly type: 'ObjectIntersectionOf';
    readonly classExpressions: readonly [ClassExpression, ClassExpression, ...ClassExpression[]];
}

/**
 * Union of class expressions (OR).
 * At least one class expression must be satisfied.
 * OWL2: ObjectUnionOf(CE1 ... CEn) - requires at least 2 operands.
 */
export interface ObjectUnionOf extends ClassExpressionBase {
    readonly type: 'ObjectUnionOf';
    readonly classExpressions: readonly [ClassExpression, ClassExpression, ...ClassExpression[]];
}

/**
 * Complement of a class expression (NOT).
 * OWL2: ObjectComplementOf(CE)
 */
export interface ObjectComplementOf extends ClassExpressionBase {
    readonly type: 'ObjectComplementOf';
    readonly classExpression: ClassExpression;
}

/**
 * Enumeration of individuals.
 * Individual must be one of the specified individuals.
 * OWL2: ObjectOneOf(a1 ... an) - requires at least 1 individual.
 */
export interface ObjectOneOf extends ClassExpressionBase {
    readonly type: 'ObjectOneOf';
    readonly individuals: readonly [Individual, ...Individual[]];
}

// =============================================================================
// Object Property Restrictions
// =============================================================================

/**
 * Existential quantification: ∃ property . filler
 * At least one value must satisfy the filler class expression.
 * OWL2: ObjectSomeValuesFrom(OPE CE)
 */
export interface ObjectSomeValuesFrom extends ClassExpressionBase {
    readonly type: 'ObjectSomeValuesFrom';
    readonly property: ObjectPropertyExpression;
    readonly filler: ClassExpression;
}

/**
 * Universal quantification: ∀ property . filler
 * All values must satisfy the filler class expression.
 * OWL2: ObjectAllValuesFrom(OPE CE)
 */
export interface ObjectAllValuesFrom extends ClassExpressionBase {
    readonly type: 'ObjectAllValuesFrom';
    readonly property: ObjectPropertyExpression;
    readonly filler: ClassExpression;
}

/**
 * Has value restriction.
 * Property must have the specified individual as a value.
 * OWL2: ObjectHasValue(OPE a)
 */
export interface ObjectHasValue extends ClassExpressionBase {
    readonly type: 'ObjectHasValue';
    readonly property: ObjectPropertyExpression;
    readonly individual: Individual;
}

/**
 * Has self restriction.
 * Property relates the individual to itself.
 * OWL2: ObjectHasSelf(OPE)
 */
export interface ObjectHasSelf extends ClassExpressionBase {
    readonly type: 'ObjectHasSelf';
    readonly property: ObjectPropertyExpression;
}

/**
 * Minimum cardinality restriction: >= n
 * At least n values (optionally qualified by a class expression).
 * OWL2: ObjectMinCardinality(n OPE) or ObjectMinCardinality(n OPE CE)
 */
export interface ObjectMinCardinality extends ClassExpressionBase {
    readonly type: 'ObjectMinCardinality';
    readonly cardinality: number;
    readonly property: ObjectPropertyExpression;
    /** Optional qualifier - for qualified cardinality restrictions */
    readonly filler?: ClassExpression;
}

/**
 * Maximum cardinality restriction: <= n
 * At most n values (optionally qualified by a class expression).
 * OWL2: ObjectMaxCardinality(n OPE) or ObjectMaxCardinality(n OPE CE)
 */
export interface ObjectMaxCardinality extends ClassExpressionBase {
    readonly type: 'ObjectMaxCardinality';
    readonly cardinality: number;
    readonly property: ObjectPropertyExpression;
    /** Optional qualifier - for qualified cardinality restrictions */
    readonly filler?: ClassExpression;
}

/**
 * Exact cardinality restriction: = n
 * Exactly n values (optionally qualified by a class expression).
 * OWL2: ObjectExactCardinality(n OPE) or ObjectExactCardinality(n OPE CE)
 */
export interface ObjectExactCardinality extends ClassExpressionBase {
    readonly type: 'ObjectExactCardinality';
    readonly cardinality: number;
    readonly property: ObjectPropertyExpression;
    /** Optional qualifier - for qualified cardinality restrictions */
    readonly filler?: ClassExpression;
}

// =============================================================================
// Data Property Restrictions
// =============================================================================

/**
 * Data existential quantification.
 * At least one data value must satisfy the data range.
 * OWL2: DataSomeValuesFrom(DPE1 ... DPEn DR) - requires at least 1 property.
 */
export interface DataSomeValuesFrom extends ClassExpressionBase {
    readonly type: 'DataSomeValuesFrom';
    readonly properties: readonly [DataPropertyExpression, ...DataPropertyExpression[]];
    readonly dataRange: DataRange;
}

/**
 * Data universal quantification.
 * All data values must satisfy the data range.
 * OWL2: DataAllValuesFrom(DPE1 ... DPEn DR) - requires at least 1 property.
 */
export interface DataAllValuesFrom extends ClassExpressionBase {
    readonly type: 'DataAllValuesFrom';
    readonly properties: readonly [DataPropertyExpression, ...DataPropertyExpression[]];
    readonly dataRange: DataRange;
}

/**
 * Data has value restriction.
 * Property must have the specified literal as a value.
 * OWL2: DataHasValue(DPE lt)
 */
export interface DataHasValue extends ClassExpressionBase {
    readonly type: 'DataHasValue';
    readonly property: DataPropertyExpression;
    readonly literal: Literal;
}

/**
 * Data minimum cardinality restriction: >= n
 * At least n data values (optionally qualified by a data range).
 * OWL2: DataMinCardinality(n DPE) or DataMinCardinality(n DPE DR)
 */
export interface DataMinCardinality extends ClassExpressionBase {
    readonly type: 'DataMinCardinality';
    readonly cardinality: number;
    readonly property: DataPropertyExpression;
    /** Optional qualifier - for qualified cardinality restrictions */
    readonly dataRange?: DataRange;
}

/**
 * Data maximum cardinality restriction: <= n
 * At most n data values (optionally qualified by a data range).
 * OWL2: DataMaxCardinality(n DPE) or DataMaxCardinality(n DPE DR)
 */
export interface DataMaxCardinality extends ClassExpressionBase {
    readonly type: 'DataMaxCardinality';
    readonly cardinality: number;
    readonly property: DataPropertyExpression;
    /** Optional qualifier - for qualified cardinality restrictions */
    readonly dataRange?: DataRange;
}

/**
 * Data exact cardinality restriction: = n
 * Exactly n data values (optionally qualified by a data range).
 * OWL2: DataExactCardinality(n DPE) or DataExactCardinality(n DPE DR)
 */
export interface DataExactCardinality extends ClassExpressionBase {
    readonly type: 'DataExactCardinality';
    readonly cardinality: number;
    readonly property: DataPropertyExpression;
    /** Optional qualifier - for qualified cardinality restrictions */
    readonly dataRange?: DataRange;
}

// =============================================================================
// Union Type
// =============================================================================

/**
 * Discriminated union of all 18 class expression types.
 * The 'type' field distinguishes between class expression kinds.
 */
export type ClassExpression =
    // Named class
    | Class
    // Boolean combinations
    | ObjectIntersectionOf
    | ObjectUnionOf
    | ObjectComplementOf
    | ObjectOneOf
    // Object property restrictions
    | ObjectSomeValuesFrom
    | ObjectAllValuesFrom
    | ObjectHasValue
    | ObjectHasSelf
    | ObjectMinCardinality
    | ObjectMaxCardinality
    | ObjectExactCardinality
    // Data property restrictions
    | DataSomeValuesFrom
    | DataAllValuesFrom
    | DataHasValue
    | DataMinCardinality
    | DataMaxCardinality
    | DataExactCardinality;

// =============================================================================
// Factory Functions
// =============================================================================

/**
 * Create a named class reference.
 */
export function owlClass(iri: IRI): Class {
    return { type: 'Class', iri };
}

/**
 * Create an intersection of class expressions.
 * Requires at least 2 operands.
 */
export function objectIntersectionOf(
    first: ClassExpression,
    second: ClassExpression,
    ...rest: ClassExpression[]
): ObjectIntersectionOf {
    return {
        type: 'ObjectIntersectionOf',
        classExpressions: [first, second, ...rest]
    };
}

/**
 * Create a union of class expressions.
 * Requires at least 2 operands.
 */
export function objectUnionOf(
    first: ClassExpression,
    second: ClassExpression,
    ...rest: ClassExpression[]
): ObjectUnionOf {
    return {
        type: 'ObjectUnionOf',
        classExpressions: [first, second, ...rest]
    };
}

/**
 * Create a complement of a class expression.
 */
export function objectComplementOf(classExpression: ClassExpression): ObjectComplementOf {
    return { type: 'ObjectComplementOf', classExpression };
}

/**
 * Create an enumeration of individuals.
 * Requires at least 1 individual.
 */
export function objectOneOf(first: Individual, ...rest: Individual[]): ObjectOneOf {
    return {
        type: 'ObjectOneOf',
        individuals: [first, ...rest]
    };
}

/**
 * Create an existential object property restriction.
 */
export function objectSomeValuesFrom(
    property: ObjectPropertyExpression,
    filler: ClassExpression
): ObjectSomeValuesFrom {
    return { type: 'ObjectSomeValuesFrom', property, filler };
}

/**
 * Create a universal object property restriction.
 */
export function objectAllValuesFrom(
    property: ObjectPropertyExpression,
    filler: ClassExpression
): ObjectAllValuesFrom {
    return { type: 'ObjectAllValuesFrom', property, filler };
}

/**
 * Create a has-value object property restriction.
 */
export function objectHasValue(
    property: ObjectPropertyExpression,
    individual: Individual
): ObjectHasValue {
    return { type: 'ObjectHasValue', property, individual };
}

/**
 * Create a has-self object property restriction.
 */
export function objectHasSelf(property: ObjectPropertyExpression): ObjectHasSelf {
    return { type: 'ObjectHasSelf', property };
}

/**
 * Create a minimum cardinality object property restriction.
 */
export function objectMinCardinality(
    cardinality: number,
    property: ObjectPropertyExpression,
    filler?: ClassExpression
): ObjectMinCardinality {
    return {
        type: 'ObjectMinCardinality',
        cardinality,
        property,
        ...(filler && { filler })
    };
}

/**
 * Create a maximum cardinality object property restriction.
 */
export function objectMaxCardinality(
    cardinality: number,
    property: ObjectPropertyExpression,
    filler?: ClassExpression
): ObjectMaxCardinality {
    return {
        type: 'ObjectMaxCardinality',
        cardinality,
        property,
        ...(filler && { filler })
    };
}

/**
 * Create an exact cardinality object property restriction.
 */
export function objectExactCardinality(
    cardinality: number,
    property: ObjectPropertyExpression,
    filler?: ClassExpression
): ObjectExactCardinality {
    return {
        type: 'ObjectExactCardinality',
        cardinality,
        property,
        ...(filler && { filler })
    };
}

/**
 * Create an existential data property restriction.
 * Requires at least 1 property.
 */
export function dataSomeValuesFrom(
    properties: readonly [DataPropertyExpression, ...DataPropertyExpression[]],
    dataRange: DataRange
): DataSomeValuesFrom {
    return { type: 'DataSomeValuesFrom', properties, dataRange };
}

/**
 * Create a universal data property restriction.
 * Requires at least 1 property.
 */
export function dataAllValuesFrom(
    properties: readonly [DataPropertyExpression, ...DataPropertyExpression[]],
    dataRange: DataRange
): DataAllValuesFrom {
    return { type: 'DataAllValuesFrom', properties, dataRange };
}

/**
 * Create a has-value data property restriction.
 */
export function dataHasValue(
    property: DataPropertyExpression,
    literal: Literal
): DataHasValue {
    return { type: 'DataHasValue', property, literal };
}

/**
 * Create a minimum cardinality data property restriction.
 */
export function dataMinCardinality(
    cardinality: number,
    property: DataPropertyExpression,
    dataRange?: DataRange
): DataMinCardinality {
    return {
        type: 'DataMinCardinality',
        cardinality,
        property,
        ...(dataRange && { dataRange })
    };
}

/**
 * Create a maximum cardinality data property restriction.
 */
export function dataMaxCardinality(
    cardinality: number,
    property: DataPropertyExpression,
    dataRange?: DataRange
): DataMaxCardinality {
    return {
        type: 'DataMaxCardinality',
        cardinality,
        property,
        ...(dataRange && { dataRange })
    };
}

/**
 * Create an exact cardinality data property restriction.
 */
export function dataExactCardinality(
    cardinality: number,
    property: DataPropertyExpression,
    dataRange?: DataRange
): DataExactCardinality {
    return {
        type: 'DataExactCardinality',
        cardinality,
        property,
        ...(dataRange && { dataRange })
    };
}

// =============================================================================
// Type Guards
// =============================================================================

// Named class
export function isClass(ce: ClassExpression): ce is Class {
    return ce.type === 'Class';
}

// Boolean combinations
export function isObjectIntersectionOf(ce: ClassExpression): ce is ObjectIntersectionOf {
    return ce.type === 'ObjectIntersectionOf';
}

export function isObjectUnionOf(ce: ClassExpression): ce is ObjectUnionOf {
    return ce.type === 'ObjectUnionOf';
}

export function isObjectComplementOf(ce: ClassExpression): ce is ObjectComplementOf {
    return ce.type === 'ObjectComplementOf';
}

export function isObjectOneOf(ce: ClassExpression): ce is ObjectOneOf {
    return ce.type === 'ObjectOneOf';
}

// Object property restrictions
export function isObjectSomeValuesFrom(ce: ClassExpression): ce is ObjectSomeValuesFrom {
    return ce.type === 'ObjectSomeValuesFrom';
}

export function isObjectAllValuesFrom(ce: ClassExpression): ce is ObjectAllValuesFrom {
    return ce.type === 'ObjectAllValuesFrom';
}

export function isObjectHasValue(ce: ClassExpression): ce is ObjectHasValue {
    return ce.type === 'ObjectHasValue';
}

export function isObjectHasSelf(ce: ClassExpression): ce is ObjectHasSelf {
    return ce.type === 'ObjectHasSelf';
}

export function isObjectMinCardinality(ce: ClassExpression): ce is ObjectMinCardinality {
    return ce.type === 'ObjectMinCardinality';
}

export function isObjectMaxCardinality(ce: ClassExpression): ce is ObjectMaxCardinality {
    return ce.type === 'ObjectMaxCardinality';
}

export function isObjectExactCardinality(ce: ClassExpression): ce is ObjectExactCardinality {
    return ce.type === 'ObjectExactCardinality';
}

// Data property restrictions
export function isDataSomeValuesFrom(ce: ClassExpression): ce is DataSomeValuesFrom {
    return ce.type === 'DataSomeValuesFrom';
}

export function isDataAllValuesFrom(ce: ClassExpression): ce is DataAllValuesFrom {
    return ce.type === 'DataAllValuesFrom';
}

export function isDataHasValue(ce: ClassExpression): ce is DataHasValue {
    return ce.type === 'DataHasValue';
}

export function isDataMinCardinality(ce: ClassExpression): ce is DataMinCardinality {
    return ce.type === 'DataMinCardinality';
}

export function isDataMaxCardinality(ce: ClassExpression): ce is DataMaxCardinality {
    return ce.type === 'DataMaxCardinality';
}

export function isDataExactCardinality(ce: ClassExpression): ce is DataExactCardinality {
    return ce.type === 'DataExactCardinality';
}

// Aggregate type guards
export function isObjectPropertyRestriction(ce: ClassExpression): ce is
    | ObjectSomeValuesFrom
    | ObjectAllValuesFrom
    | ObjectHasValue
    | ObjectHasSelf
    | ObjectMinCardinality
    | ObjectMaxCardinality
    | ObjectExactCardinality {
    return (
        ce.type === 'ObjectSomeValuesFrom' ||
        ce.type === 'ObjectAllValuesFrom' ||
        ce.type === 'ObjectHasValue' ||
        ce.type === 'ObjectHasSelf' ||
        ce.type === 'ObjectMinCardinality' ||
        ce.type === 'ObjectMaxCardinality' ||
        ce.type === 'ObjectExactCardinality'
    );
}

export function isDataPropertyRestriction(ce: ClassExpression): ce is
    | DataSomeValuesFrom
    | DataAllValuesFrom
    | DataHasValue
    | DataMinCardinality
    | DataMaxCardinality
    | DataExactCardinality {
    return (
        ce.type === 'DataSomeValuesFrom' ||
        ce.type === 'DataAllValuesFrom' ||
        ce.type === 'DataHasValue' ||
        ce.type === 'DataMinCardinality' ||
        ce.type === 'DataMaxCardinality' ||
        ce.type === 'DataExactCardinality'
    );
}

export function isBooleanCombination(ce: ClassExpression): ce is
    | ObjectIntersectionOf
    | ObjectUnionOf
    | ObjectComplementOf
    | ObjectOneOf {
    return (
        ce.type === 'ObjectIntersectionOf' ||
        ce.type === 'ObjectUnionOf' ||
        ce.type === 'ObjectComplementOf' ||
        ce.type === 'ObjectOneOf'
    );
}
